import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, Star, Users, MapPin } from 'lucide-react';
import api from '@/lib/api';

/**
 * SocialProofSection — landing-page social-proof block.
 *
 *  • Stats Bar: live member count, distinct event-city count, static rating.
 *    Admin can override counts via /api/admin/landing/stats.
 *  • Testimonials: 3-card row (responsive), content from /api/testimonials.
 *
 * Public-readable endpoints — no auth required.
 */
export default function SocialProofSection() {
  const [stats, setStats] = useState(null);
  const [testimonials, setTestimonials] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get('/landing/stats'),
          api.get('/testimonials'),
        ]);
        if (cancelled) return;
        setStats(s.data);
        setTestimonials(t.data.testimonials || []);
      } catch {
        // graceful fallback — render section with hardcoded copy if API fails
        if (cancelled) return;
        setStats({
          members_label: '200+ California Moms',
          cities_label: 'Events in 5 Cities',
          rating_label: '5-Star Community Rating',
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const safeStats = stats || {
    members_label: '200+ California Moms',
    cities_label: 'Events in 5 Cities',
    rating_label: '5-Star Community Rating',
  };

  return (
    <section
      className="py-16 md:py-24 px-6 md:px-12 bg-seafoam/30"
      data-testid="social-proof-section"
    >
      <div className="max-w-7xl mx-auto">
        {/* ─── Stats Bar ─── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 sm:grid-cols-3 gap-6 md:gap-12 text-center mb-16 md:mb-20"
          data-testid="social-proof-stats-bar"
        >
          {[
            { icon: Users, label: safeStats.members_label, testId: 'stat-members' },
            { icon: MapPin, label: safeStats.cities_label, testId: 'stat-cities' },
            { icon: Star, label: safeStats.rating_label, testId: 'stat-rating' },
          ].map((s, i) => (
            <motion.div
              key={s.testId}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="flex flex-col items-center gap-2"
              data-testid={s.testId}
            >
              <s.icon className="text-teal" size={26} />
              <p className="font-display text-xl md:text-2xl text-navy">
                {s.label}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* ─── Testimonials Headline ─── */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-10"
        >
          <h2 className="font-display text-3xl md:text-4xl text-navy">
            What our moms are <span className="italic-teal">saying</span>
          </h2>
        </motion.div>

        {/* ─── Testimonial Cards ─── */}
        {testimonials.length > 0 ? (
          <div className="grid md:grid-cols-3 gap-5 md:gap-6" data-testid="testimonials-grid">
            {testimonials.slice(0, 3).map((t, i) => (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="bg-white rounded-2xl p-7 shadow-card hover:shadow-floating transition-shadow"
                data-testid={`testimonial-card-${t.id}`}
              >
                <Quote className="text-seafoam mb-3" size={28} />
                <p className="text-text-mid leading-relaxed mb-6 text-sm md:text-base">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <p className="font-semibold text-navy text-sm">{t.name}</p>
                <p className="text-text-light text-xs">{t.city}</p>
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-center text-text-light text-sm" data-testid="testimonials-empty">
            Testimonials coming soon.
          </p>
        )}
      </div>
    </section>
  );
}
